import { Button, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Clock,
  FileSearch,
  History,
  ImageIcon,
  LockIcon,
  PackageIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopInventoryStockRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import {
  CATALOG_FILTER_CATEGORY_FIELD,
  CATALOG_FILTER_Q_FIELD,
} from "@/modules/merchant/presentation/ui/ShopCatalogFilter";
import { ShopInventoryAdjustModal } from "@/modules/merchant/presentation/ui/ShopInventoryAdjustModal";
import { ShopInventoryAdjustPickModal } from "@/modules/merchant/presentation/ui/ShopInventoryAdjustPickModal";
import { ShopInventoryQuickActions } from "@/modules/merchant/presentation/ui/ShopInventoryQuickActions";
import { WarehouseLocationScanModal } from "@/modules/merchant/presentation/ui/WarehouseLocationScanModal";
import {
  INVENTORY_FILTER_BRANCH_FIELD,
  INVENTORY_FILTER_LOCATION_FIELD,
  ShopInventoryFilter,
} from "@/modules/merchant/presentation/ui/ShopInventoryFilter";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { findCondition } from "@/shared/contracts/query-helpers";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { type RowAction, RowActions } from "@/shared/ui/RowActions";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import {
  useShopInventoryStockQuery,
  useShopInventorySummaryQuery,
} from "../api/queries";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const INVENTORY_RESERVE_ROUTE = "/app/shops/$shopId/inventory/reserve" as const;
const INVENTORY_MOVEMENTS_ROUTE =
  "/app/shops/$shopId/inventory/movements" as const;
const INVENTORY_SKU_DETAIL_ROUTE =
  "/app/shops/$shopId/inventory/skus/$shopProductSkuId" as const;

export function ShopInventoryPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: INVENTORY_ROUTE });
  const search = (useSearch({
    from: INVENTORY_ROUTE,
  }) ?? {}) as OffsetPageQueryDTO;

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const qFilter = findCondition(filters, CATALOG_FILTER_Q_FIELD);
  const categoryFilter = findCondition(filters, CATALOG_FILTER_CATEGORY_FIELD);
  const branchFilter = findCondition(filters, INVENTORY_FILTER_BRANCH_FIELD);
  const locationFilter = findCondition(filters, INVENTORY_FILTER_LOCATION_FIELD);
  const q =
    typeof qFilter?.value === "string" && qFilter.value.trim() !== ""
      ? qFilter.value.trim()
      : undefined;
  const categoryId =
    categoryFilter?.value != null && String(categoryFilter.value).trim() !== ""
      ? String(categoryFilter.value)
      : undefined;
  const branchId =
    branchFilter?.value != null && String(branchFilter.value).trim() !== ""
      ? String(branchFilter.value)
      : undefined;
  const locationId =
    locationFilter?.value != null && String(locationFilter.value).trim() !== ""
      ? String(locationFilter.value)
      : undefined;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  const [adjustRow, setAdjustRow] = useState<ShopInventoryStockRow | null>(
    null,
  );
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustPickOpen, setAdjustPickOpen] = useState(false);
  const [locationScanOpen, setLocationScanOpen] = useState(false);

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );
  const canReceive = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.receive,
    verifiedLevel,
  );
  const canTransfer = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.transfer,
    verifiedLevel,
  );

  const summary = useShopInventorySummaryQuery(shopId);
  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);
  const formatSummaryCount = (n: number | undefined) =>
    summary.isLoading ? "—" : numberFmt.format(n ?? 0);

  const list = useShopInventoryStockQuery(shopId, {
    offset,
    limit,
    q,
    categoryId,
    branchId,
    locationId: warehouseLocationsOn && branchId ? locationId : undefined,
  });
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;

  const columns: TanstackReactTable.ColumnDef<ShopInventoryStockRow>[] =
    useMemo(
      () => [
        {
          id: "product",
          header: "ສິນຄ້າ",
          cell: ({ row }) => {
            const r = row.original;
            const url = resolveImageSrc(r.imageUrl);
            return (
              <div className="flex min-w-0 max-w-xs items-start gap-2">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
                  {url ? (
                    <img
                      src={url}
                      alt={r.productName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <ImageIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm leading-snug">
                    {r.productName}
                  </p>
                  {r.brandName ? (
                    <p className="mt-0.5 text-muted-foreground text-xs leading-snug">
                      {r.brandName}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          },
          size: 240,
        },
        {
          id: "categoryName",
          header: "ໝວດ",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-sm">
              {row.original.categoryName ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "branch",
          header: "ສາຂາ",
          cell: ({ row }) => {
            const n = row.original.branchName;
            if (!n) {
              return <span className="text-muted-foreground text-sm">—</span>;
            }
            return <span className="text-sm">{n}</span>;
          },
          size: 120,
        },
        {
          id: "shopSku",
          header: "SKU",
          cell: ({ row }) => (
            <span className="font-mono text-muted-foreground text-sm">
              {row.original.shopSku}
            </span>
          ),
          size: 100,
        },
        {
          id: "barcode",
          header: "Barcode",
          cell: ({ row }) => (
            <span className="font-mono text-muted-foreground text-sm">
              {row.original.barcode ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "tracked",
          header: "ຕິດຕາມ",
          cell: ({ row }) =>
            row.original.inventoryTracked ? (
              <span className="font-normal text-green-500">
                <CheckCircle className="size-4" aria-hidden />
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">—</span>
            ),
          size: 80,
        },
        {
          id: "onHand",
          header: "ຄົງເຫຼືອ",
          cell: ({ row }) => {
            const v = row.original.quantityOnHand;
            if (v === null) {
              return <span className="text-muted-foreground text-sm">—</span>;
            }
            return (
              <span className="font-medium text-sm tabular-nums">{v}</span>
            );
          },
          size: 88,
        },
        {
          id: "reserved",
          header: "ຈອງ",
          cell: ({ row }) => {
            const v = row.original.quantityReserved;
            if (v === null) {
              return <span className="text-muted-foreground text-sm">—</span>;
            }
            return (
              <span className="text-muted-foreground text-sm tabular-nums">
                {v}
              </span>
            );
          },
          size: 72,
        },
        ...(warehouseLocationsOn && branchId
          ? [
              {
                id: "location",
                header: "ຕຳແໜ່ງ",
                cell: ({ row }) => {
                  const s = row.original.locationSummary;
                  if (!s) {
                    return (
                      <span className="text-muted-foreground text-sm">—</span>
                    );
                  }
                  return (
                    <span
                      className={
                        s.includes("ຍັງບໍ່")
                          ? "text-amber-700 text-sm dark:text-amber-400"
                          : "text-sm"
                      }
                    >
                      {s}
                    </span>
                  );
                },
                size: 120,
              } as TanstackReactTable.ColumnDef<ShopInventoryStockRow>,
            ]
          : []),
        ...(shopId
          ? [
              {
                id: "actions",
                size: 96,
                cell: ({ row }) => {
                  const r = row.original;
                  const detailBranchId = r.branchId ?? branchId;
                  if (!detailBranchId) {
                    return (
                      <span className="text-muted-foreground text-xs">—</span>
                    );
                  }
                  const actions: RowAction[] = [
                    {
                      label: "ລາຍລະອຽດ",
                      variant: "secondary",
                      icon: <FileSearch className="size-4" aria-hidden />,
                      onClick: () => {
                        void nav({
                          to: INVENTORY_SKU_DETAIL_ROUTE,
                          params: {
                            shopId,
                            shopProductSkuId: r.shopProductSkuId,
                          },
                          search: { branchId: detailBranchId },
                        });
                      },
                    },
                  ];
                  return <RowActions actions={actions} maxInline={1} />;
                },
              } as TanstackReactTable.ColumnDef<ShopInventoryStockRow>,
            ]
          : []),
      ],
      [branchId, shopId, nav, warehouseLocationsOn],
    );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventory(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-heading font-semibold text-xl tracking-tight">
              <PackageIcon className="size-6 text-primary" aria-hidden />
              ສາງ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຕາມສາຂາ ແລະ SKU — ກອງຕາມສາຂາໄດ້; ຮັບເຂົ້າ / ປັບ /
              ໂອນລະຫວ່າງສາຂາຕາມສິດ
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {shopId ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  nav({
                    to: INVENTORY_MOVEMENTS_ROUTE,
                    params: { shopId },
                    search: {
                      offset: 0,
                      limit: 20,
                      branchId: undefined,
                      shopProductSkuId: undefined,
                      type: undefined,
                      createdFrom: undefined,
                      createdTo: undefined,
                      recOnlyMismatch: true,
                    },
                  });
                }}
              >
                <History className="size-4" aria-hidden />
                ປະຫວັດສະຕ໋ອກ
              </Button>
            ) : null}
            {canAdjust && shopId ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  nav({
                    to: INVENTORY_RESERVE_ROUTE,
                    params: { shopId },
                  });
                }}
              >
                <LockIcon className="size-4" aria-hidden />
                ຈອງ / ປົດຈອງ
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="ລວມ SKU ທັງໝົດ"
            value={formatSummaryCount(summary.data?.totalSkus)}
            description="ຈຳນວນ SKU ຂາຍໄດ້ທັງໝົດໃນຮ້ານນີ້"
            icon={<PackageIcon className="size-5" aria-hidden />}
          />
          <SummaryCard
            title="ແຈ້ງເຕືອນສະຕ໋ອກຕໍ່າ"
            value={formatSummaryCount(summary.data?.lowStockAlerts)}
            description="ແຖວສະຕ໋ອກທີ່ເຫຼືອ ≤ 5 ຊິ້ນຕໍ່ສາຂາ"
            icon={<AlertTriangle className="size-5" aria-hidden />}
          />
          <SummaryCard
            title="ອັບເດດຫຼ້າສຸດ"
            value={formatSummaryCount(summary.data?.recentUpdates)}
            description="ຈຳນວນແຖວສະຕ໋ອກທີ່ປ່ຽນໃນ 7 ວັນຜ່ານມາ"
            icon={<Clock className="size-5" aria-hidden />}
          />
          <SummaryCard
            title="ສາຂາເປີດໃຊ້"
            value={formatSummaryCount(summary.data?.activeBranches)}
            description="ສາຂາທີ່ສະຖານະເປີດໃຊ້ງານໃນຮ້ານນີ້"
            icon={<Building2 className="size-5" aria-hidden />}
          />
        </div>

        {shopId ? (
          <ShopInventoryQuickActions
            shopId={shopId}
            canReceive={canReceive}
            canTransfer={canTransfer}
            canAdjust={canAdjust}
            onAdjustClick={() => setAdjustPickOpen(true)}
            canScanLocation={warehouseLocationsOn}
            onScanLocationClick={() => setLocationScanOpen(true)}
          />
        ) : null}

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopInventoryFilter />
          <DataTable<ShopInventoryStockRow, unknown>
            noDataMessage="ບໍ່ມີລາຍການທີ່ກົງກັບຕົວກອງ."
            isLoading={list.isLoading}
            columns={columns}
            data={rows}
            offset={offset}
            limit={limit}
            totalCount={total}
            onPaginationChange={(p) => {
              if (!shopId) return;
              nav({
                to: INVENTORY_ROUTE,
                params: { shopId },
                search: { ...search, offset: p.offset, limit: p.limit },
              });
            }}
          />
        </div>

        {shopId ? (
          <>
            <ShopInventoryAdjustPickModal
              open={adjustPickOpen}
              onOpenChange={setAdjustPickOpen}
              shopId={shopId}
              onPick={(row) => {
                setAdjustRow(row);
                setAdjustOpen(true);
              }}
            />
            <ShopInventoryAdjustModal
              open={adjustOpen}
              onOpenChange={(o) => {
                setAdjustOpen(o);
                if (!o) setAdjustRow(null);
              }}
              shopId={shopId}
              row={adjustRow}
            />
            <WarehouseLocationScanModal
              shopId={shopId}
              branchId={branchId}
              open={locationScanOpen}
              onOpenChange={setLocationScanOpen}
              canAdjust={canAdjust}
            />
          </>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
